import React, { useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useParallax } from 'react-scroll-parallax';

gsap.registerPlugin(ScrollTrigger);

const neon = { a: '#F47D11', b: '#F4733A' };

// ─── Radar / Spider Chart Card (CLIENTS) ──────────────────────────────────────
const RadarChartCard = ({ stat, className = '' }) => {
  const cardRef = useRef(null);
  const valueRef = useRef(null);
  const polygonRef = useRef(null);
  const dotRefs = useRef([]);

  const cx = 110, cy = 110, r = 84;
  const axes = ['Network', 'Cloud', 'Security', 'SMS', 'Voice', 'Support'];
  const values = [0.85, 0.92, 0.78, 0.95, 0.88, 0.82];
  const N = axes.length;
  const gridLevels = [0.25, 0.5, 0.75, 1.0];

  const getPoint = (i, v) => {
    const angle = (2 * Math.PI * i) / N - Math.PI / 2;
    return { x: cx + r * v * Math.cos(angle), y: cy + r * v * Math.sin(angle) };
  };

  const dataPointsStr = values
    .map((v, i) => { const p = getPoint(i, v); return `${p.x},${p.y}`; })
    .join(' ');

  useEffect(() => {
    const el = cardRef.current;
    const valueEl = valueRef.current;
    if (!el || !valueEl) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(el, { opacity: 0, y: 30 }, {
        opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
      });
      const obj = { v: 0 };
      gsap.to(obj, {
        v: stat.value, duration: 1.6, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
        onUpdate: () => { valueEl.textContent = `${Math.floor(obj.v)}${stat.suffix ?? ''}`; },
      });
      if (polygonRef.current) {
        gsap.fromTo(polygonRef.current,
          { scale: 0, opacity: 0, transformOrigin: `${cx}px ${cy}px` },
          {
            scale: 1, opacity: 1, duration: 1.3, ease: 'elastic.out(1, 0.5)', delay: 0.35,
            scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
          }
        );
      }
      const dots = dotRefs.current.filter(Boolean);
      if (dots.length) {
        gsap.fromTo(dots, { scale: 0, opacity: 0 }, {
          scale: 1, opacity: 1, duration: 0.5, stagger: 0.08, ease: 'back.out(2)', delay: 0.9,
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
        });
      }
    }, el);
    return () => ctx.revert();
  }, [stat.value, stat.suffix]);

  return (
    <motion.div
      ref={cardRef}
      data-telemetry-card="true"
      className={`group relative overflow-hidden rounded-2xl border border-gray-800/60 bg-gray-950/20 backdrop-blur-md p-5 flex flex-col min-h-0 h-full ${className}`}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_30%_20%,rgba(244,125,17,0.2),transparent_50%)]" />
      <div className="relative flex items-start justify-between gap-3 flex-shrink-0">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-400">{stat.kicker}</div>
          <div ref={valueRef} className="mt-1 text-3xl font-black text-white">0{stat.suffix ?? ''}</div>
          <div className="mt-1 text-xs text-gray-400 leading-relaxed">{stat.label}</div>
        </div>
        <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-[#F47D11]/15 to-[#F4733A]/15 border border-[#F47D11]/20 flex items-center justify-center text-lg">
          {stat.icon}
        </div>
      </div>

      <div className="mt-3 flex flex-1 min-h-[220px] sm:min-h-[260px] lg:min-h-[280px] items-center justify-center">
        <svg viewBox="-22 -22 264 264" width="100%" className="max-h-[min(340px,55vw)] w-full h-auto" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <defs>
            <linearGradient id="radarFill" x1="0" y1="0" x2="1" y2="1">
              <stop stopColor={neon.a} stopOpacity="0.4" />
              <stop offset="1" stopColor={neon.b} stopOpacity="0.15" />
            </linearGradient>
            <filter id="radarGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {gridLevels.map((level, li) => (
            <polygon
              key={li}
              points={axes.map((_, i) => { const p = getPoint(i, level); return `${p.x},${p.y}`; }).join(' ')}
              fill="none"
              stroke="rgba(244,125,17,0.14)"
              strokeWidth="1"
            />
          ))}

          {axes.map((_, i) => {
            const p = getPoint(i, 1);
            return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="rgba(244,125,17,0.18)" strokeWidth="1" />;
          })}

          <polygon
            ref={polygonRef}
            points={dataPointsStr}
            fill="url(#radarFill)"
            stroke={neon.a}
            strokeWidth="2.5"
            strokeLinejoin="round"
            filter="url(#radarGlow)"
            style={{ transformOrigin: `${cx}px ${cy}px` }}
          />

          {values.map((v, i) => {
            const p = getPoint(i, v);
            return (
              <circle
                key={i}
                ref={(el) => { dotRefs.current[i] = el; }}
                cx={p.x} cy={p.y} r="4.5"
                fill={neon.a}
                filter="url(#radarGlow)"
                style={{ transformOrigin: `${p.x}px ${p.y}px` }}
              />
            );
          })}

          {axes.map((label, i) => {
            const p = getPoint(i, 1.28);
            return (
              <text
                key={i} x={p.x} y={p.y}
                textAnchor="middle" dominantBaseline="middle"
                fill="rgba(255,255,255,0.6)" fontSize="10.5" fontWeight="700" letterSpacing="0.3"
              >
                {label.toUpperCase()}
              </text>
            );
          })}

          {values.map((v, i) => {
            const p = getPoint(i, v * 0.7);
            return (
              <text key={i} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="middle"
                fill="rgba(255,255,255,0.45)" fontSize="7.5"
              >
                {Math.round(v * 100)}
              </text>
            );
          })}

          <circle cx={cx} cy={cy} r="3.5" fill={neon.b} opacity="0.7" />
        </svg>
      </div>

      <div className="mt-3 pt-2 border-t border-gray-800/40 grid grid-cols-3 gap-2 flex-shrink-0">
        {axes.slice(0, 3).map((label, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: neon.a }} />
            <span className="text-[10px] text-gray-500">{label}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// ─── Donut / Pie Chart Card (DEPLOYMENTS) ─────────────────────────────────────
const DonutChartCard = ({ stat, className = '' }) => {
  const cardRef = useRef(null);
  const valueRef = useRef(null);
  const arcRefs = useRef([]);

  const cx = 80, cy = 80, outerR = 76, innerR = 44;
  const segments = [
    { label: 'Network', pct: 0.30, color: neon.a },
    { label: 'Cloud Setup', pct: 0.25, color: '#F4A35C' },
    { label: 'SMS Platform', pct: 0.25, color: neon.b },
    { label: 'Custom Dev', pct: 0.20, color: '#CC5C14' },
  ];

  const describeDonutSector = (r1, r2, start, end) => {
    const GAP = 0.035;
    const s = start + GAP, e = end - GAP;
    const os = { x: cx + r2 * Math.cos(s), y: cy + r2 * Math.sin(s) };
    const oe = { x: cx + r2 * Math.cos(e), y: cy + r2 * Math.sin(e) };
    const is = { x: cx + r1 * Math.cos(e), y: cy + r1 * Math.sin(e) };
    const ie = { x: cx + r1 * Math.cos(s), y: cy + r1 * Math.sin(s) };
    const large = e - s > Math.PI ? 1 : 0;
    return `M ${os.x} ${os.y} A ${r2} ${r2} 0 ${large} 1 ${oe.x} ${oe.y} L ${is.x} ${is.y} A ${r1} ${r1} 0 ${large} 0 ${ie.x} ${ie.y} Z`;
  };

  let cumAngle = -Math.PI / 2;
  const sectorPaths = segments.map((seg) => {
    const start = cumAngle;
    const end = cumAngle + seg.pct * 2 * Math.PI;
    cumAngle = end;
    return { ...seg, path: describeDonutSector(innerR, outerR, start, end) };
  });

  useEffect(() => {
    const el = cardRef.current;
    const valueEl = valueRef.current;
    if (!el || !valueEl) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(el, { opacity: 0, y: 30 }, {
        opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
      });
      const obj = { v: 0 };
      gsap.to(obj, {
        v: stat.value, duration: 1.6, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
        onUpdate: () => { valueEl.textContent = `${Math.floor(obj.v)}${stat.suffix ?? ''}`; },
      });
      arcRefs.current.filter(Boolean).forEach((arc, i) => {
        gsap.fromTo(arc,
          { scale: 0, opacity: 0, transformOrigin: `${cx}px ${cy}px` },
          {
            scale: 1, opacity: 1, duration: 0.75, delay: 0.2 + i * 0.1, ease: 'back.out(1.6)',
            scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
          }
        );
      });
    }, el);
    return () => ctx.revert();
  }, [stat.value, stat.suffix]);

  return (
    <motion.div
      ref={cardRef}
      data-telemetry-card="true"
      className={`group relative overflow-hidden rounded-2xl border border-gray-800/60 bg-gray-950/20 backdrop-blur-md p-5 flex flex-col min-h-0 h-full ${className}`}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_80%_20%,rgba(244,125,17,0.2),transparent_50%)]" />
      <div className="relative flex items-start justify-between gap-3 flex-shrink-0">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-400">{stat.kicker}</div>
          <div ref={valueRef} className="mt-1 text-3xl font-black text-white">0{stat.suffix ?? ''}</div>
          <div className="mt-1 text-xs text-gray-400 leading-relaxed">{stat.label}</div>
        </div>
        <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-[#F47D11]/15 to-[#F4733A]/15 border border-[#F47D11]/20 flex items-center justify-center text-lg">
          {stat.icon}
        </div>
      </div>

      <div className="mt-4 flex flex-1 min-h-[220px] lg:min-h-[260px] flex-col sm:flex-row items-stretch gap-4 sm:gap-4">
        <div className="flex-1 min-w-0 min-h-[200px] sm:min-h-0 h-full flex items-center justify-center p-1 sm:p-2">
          <svg
            viewBox="0 0 160 160"
            className="w-full h-full max-h-[min(340px,52vw)] max-w-[min(340px,100%)] aspect-square shrink-0"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            <defs>
              <filter id="donutGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3.5" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            {sectorPaths.map((seg, i) => (
              <path
                key={i}
                ref={(el) => { arcRefs.current[i] = el; }}
                d={seg.path}
                fill={seg.color}
                filter="url(#donutGlow)"
                style={{ transformOrigin: `${cx}px ${cy}px` }}
              />
            ))}
            <circle cx={cx} cy={cy} r={innerR - 6} fill="rgba(6,6,14,0.92)" />
            <text x={cx} y={cy - 7} textAnchor="middle" fill="rgba(255,255,255,0.92)" fontSize="13" fontWeight="800">
              Project
            </text>
            <text x={cx} y={cy + 12} textAnchor="middle" fill="rgba(255,255,255,0.92)" fontSize="13" fontWeight="800">
              Mix
            </text>
          </svg>
        </div>

        <div className="flex flex-col gap-2.5 min-w-0 w-full sm:w-[44%] sm:max-w-[220px] flex-shrink-0 justify-center py-1 border-t border-gray-800/40 pt-4 sm:border-t-0 sm:border-l sm:pt-1 sm:pl-4">
          {sectorPaths.map((seg, i) => (
            <div key={i} className="flex items-center gap-2 min-w-0">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: seg.color, boxShadow: `0 0 6px ${seg.color}` }} />
              <div className="text-xs text-gray-300 truncate">{seg.label}</div>
              <div className="text-xs font-bold text-white ml-auto pl-1 flex-shrink-0">{Math.round(seg.pct * 100)}%</div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// ─── Horizontal Bar Chart Card (SMS DELIVERED) ────────────────────────────────
const HorizontalBarChartCard = ({ stat }) => {
  const cardRef = useRef(null);
  const valueRef = useRef(null);
  const barRefs = useRef([]);

  const categories = [
    { label: 'West Africa', value: 0.92, count: '230M' },
    { label: 'East Africa', value: 0.74, count: '148M' },
    { label: 'North Africa', value: 0.56, count: '86M' },
    { label: 'SADC Region', value: 0.64, count: '36M' },
  ];

  useEffect(() => {
    const el = cardRef.current;
    const valueEl = valueRef.current;
    if (!el || !valueEl) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(el, { opacity: 0, y: 30 }, {
        opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
      });
      const obj = { v: 0 };
      gsap.to(obj, {
        v: stat.value, duration: 1.6, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
        onUpdate: () => { valueEl.textContent = `${Math.floor(obj.v)}${stat.suffix ?? ''}`; },
      });
      barRefs.current.filter(Boolean).forEach((bar, i) => {
        gsap.fromTo(bar,
          { scaleX: 0, transformOrigin: 'left center' },
          {
            scaleX: 1, duration: 1.0, delay: 0.15 + i * 0.12, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
          }
        );
      });
    }, el);
    return () => ctx.revert();
  }, [stat.value, stat.suffix]);

  return (
    <motion.div
      ref={cardRef}
      data-telemetry-card="true"
      className="group relative overflow-hidden rounded-2xl border border-gray-800/60 bg-gray-950/20 backdrop-blur-md p-5 flex flex-col h-full min-h-0"
      whileHover={{ y: -6 }}
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_50%_10%,rgba(244,125,17,0.2),transparent_50%)]" />
      <div className="relative flex items-start justify-between gap-3 flex-shrink-0">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-400">{stat.kicker}</div>
          <div ref={valueRef} className="mt-1 text-3xl font-black text-white">0{stat.suffix ?? ''}</div>
          <div className="mt-1 text-xs text-gray-400 leading-relaxed">{stat.label}</div>
        </div>
        <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-[#F47D11]/15 to-[#F4733A]/15 border border-[#F47D11]/20 flex items-center justify-center text-lg">
          {stat.icon}
        </div>
      </div>

      <div className="mt-4 flex-1 flex flex-col justify-center gap-3.5 min-h-0">
        {categories.map((cat, i) => (
          <div key={i}>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs font-medium text-gray-300">{cat.label}</span>
              <span className="text-xs font-bold" style={{ color: neon.a }}>{cat.count}</span>
            </div>
            <div className="relative h-3 rounded-full bg-gray-800/70 overflow-hidden">
              <div
                ref={(el) => { barRefs.current[i] = el; }}
                className="h-full rounded-full"
                style={{
                  width: `${cat.value * 100}%`,
                  background: `linear-gradient(90deg, ${neon.a} 0%, ${neon.b} 60%, #FFAA55 100%)`,
                  boxShadow: `0 0 14px rgba(244,125,17,0.55), inset 0 1px 0 rgba(255,255,255,0.15)`,
                  transformOrigin: 'left center',
                }}
              />
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 100%)',
                  pointerEvents: 'none',
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-800/50 flex justify-between items-center flex-shrink-0">
        <span className="text-xs text-gray-500 uppercase tracking-wider">Regional Coverage</span>
        <span className="inline-flex items-center gap-1.5 text-xs text-green-400">
          <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
          Live
        </span>
      </div>
    </motion.div>
  );
};

// ─── Sunburst / Concentric Ring Card (VOICE CALLS) ────────────────────────────
const SunburstChartCard = ({ stat }) => {
  const cardRef = useRef(null);
  const valueRef = useRef(null);
  const innerArcRefs = useRef([]);
  const outerArcRefs = useRef([]);

  const cx = 80, cy = 80;
  const innerR1 = 26, innerR2 = 46;
  const outerR1 = 50, outerR2 = 72;

  const innerData = [
    { label: 'Inbound', pct: 0.42, color: neon.a },
    { label: 'Outbound', pct: 0.58, color: '#F4A35C' },
  ];
  const outerData = [
    { label: 'IVR', pct: 0.22, color: '#CC5C14' },
    { label: 'Live Agent', pct: 0.20, color: neon.b },
    { label: 'Bulk Voice', pct: 0.35, color: '#E8741A' },
    { label: 'Campaign', pct: 0.23, color: '#F4A35C' },
  ];

  const makeArcPath = (r1, r2, totalPct, cumPct) => {
    const GAP = 0.028;
    const start = cumPct * 2 * Math.PI - Math.PI / 2 + GAP;
    const end = (cumPct + totalPct) * 2 * Math.PI - Math.PI / 2 - GAP;
    const os = { x: cx + r2 * Math.cos(start), y: cy + r2 * Math.sin(start) };
    const oe = { x: cx + r2 * Math.cos(end), y: cy + r2 * Math.sin(end) };
    const is = { x: cx + r1 * Math.cos(end), y: cy + r1 * Math.sin(end) };
    const ie = { x: cx + r1 * Math.cos(start), y: cy + r1 * Math.sin(start) };
    const large = (end - start) > Math.PI ? 1 : 0;
    return `M ${os.x} ${os.y} A ${r2} ${r2} 0 ${large} 1 ${oe.x} ${oe.y} L ${is.x} ${is.y} A ${r1} ${r1} 0 ${large} 0 ${ie.x} ${ie.y} Z`;
  };

  let innerCum = 0;
  const innerSectors = innerData.map((d) => {
    const path = makeArcPath(innerR1, innerR2, d.pct, innerCum);
    innerCum += d.pct;
    return { ...d, path };
  });

  let outerCum = 0;
  const outerSectors = outerData.map((d) => {
    const path = makeArcPath(outerR1, outerR2, d.pct, outerCum);
    outerCum += d.pct;
    return { ...d, path };
  });

  useEffect(() => {
    const el = cardRef.current;
    const valueEl = valueRef.current;
    if (!el || !valueEl) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(el, { opacity: 0, y: 30 }, {
        opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
      });
      const obj = { v: 0 };
      gsap.to(obj, {
        v: stat.value, duration: 1.6, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
        onUpdate: () => { valueEl.textContent = `${Math.floor(obj.v)}${stat.suffix ?? ''}`; },
      });
      const allArcs = [...innerArcRefs.current, ...outerArcRefs.current].filter(Boolean);
      gsap.fromTo(allArcs,
        { scale: 0, opacity: 0, transformOrigin: `${cx}px ${cy}px` },
        {
          scale: 1, opacity: 1, duration: 1.0, stagger: 0.07, ease: 'elastic.out(1, 0.55)', delay: 0.25,
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
        }
      );
    }, el);
    return () => ctx.revert();
  }, [stat.value, stat.suffix]);

  return (
    <motion.div
      ref={cardRef}
      data-telemetry-card="true"
      className="group relative overflow-hidden rounded-2xl border border-gray-800/60 bg-gray-950/20 backdrop-blur-md p-5 flex flex-col h-full min-h-0"
      whileHover={{ y: -6 }}
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_50%_80%,rgba(244,125,17,0.2),transparent_50%)]" />
      <div className="relative flex items-start justify-between gap-3 flex-shrink-0">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-400">{stat.kicker}</div>
          <div ref={valueRef} className="mt-1 text-3xl font-black text-white">0{stat.suffix ?? ''}</div>
          <div className="mt-1 text-xs text-gray-400 leading-relaxed">{stat.label}</div>
        </div>
        <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-[#F47D11]/15 to-[#F4733A]/15 border border-[#F47D11]/20 flex items-center justify-center text-lg">
          {stat.icon}
        </div>
      </div>

      <div className="mt-3 flex flex-1 min-h-[170px] items-center gap-2 sm:gap-3">
        <div className="flex-shrink-0 flex items-center justify-center">
          <svg viewBox="0 0 160 160" width="158" height="158" className="max-w-full h-auto w-[min(158px,40vw)] sm:w-[158px]" aria-hidden="true">
            <defs>
              <filter id="sunGlow" x="-25%" y="-25%" width="150%" height="150%">
                <feGaussianBlur stdDeviation="2.5" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            {outerSectors.map((s, i) => (
              <path
                key={`o${i}`}
                ref={(el) => { outerArcRefs.current[i] = el; }}
                d={s.path}
                fill={s.color}
                opacity="0.8"
                filter="url(#sunGlow)"
                style={{ transformOrigin: `${cx}px ${cy}px` }}
              />
            ))}
            {innerSectors.map((s, i) => (
              <path
                key={`i${i}`}
                ref={(el) => { innerArcRefs.current[i] = el; }}
                d={s.path}
                fill={s.color}
                opacity="0.95"
                filter="url(#sunGlow)"
                style={{ transformOrigin: `${cx}px ${cy}px` }}
              />
            ))}
            <circle cx={cx} cy={cy} r={innerR1 - 4} fill="rgba(6,6,14,0.95)" />
            <text x={cx} y={cy - 5} textAnchor="middle" fill="rgba(255,255,255,0.9)" fontSize="9" fontWeight="800">CALL</text>
            <text x={cx} y={cy + 8} textAnchor="middle" fill="rgba(255,255,255,0.9)" fontSize="9" fontWeight="800">FLOW</text>
          </svg>
        </div>

        <div className="flex flex-col gap-1.5 min-w-0 flex-1 justify-center py-0.5">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">Traffic Split</div>
          {[...innerSectors, ...outerSectors].map((s, i) => (
            <div key={i} className="flex items-center gap-1.5 min-w-0">
              <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: s.color, boxShadow: `0 0 5px ${s.color}` }} />
              <div className="text-xs text-gray-300 truncate">{s.label}</div>
              <div className="text-xs font-bold text-white ml-auto pl-1 flex-shrink-0">{Math.round(s.pct * 100)}%</div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// ─── Uptime Area / Line Chart Card ────────────────────────────────────────────
const UptimeAreaCard = ({ stat }) => {
  const cardRef = useRef(null);
  const valueRef = useRef(null);
  const clipRectRef = useRef(null);

  const monthlyData = [99.8, 99.9, 99.7, 99.9, 99.9, 100, 99.9, 99.8, 100, 99.9, 99.9, 100];
  const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const W = 280, H = 72;
  const yMin = 99.5, yMax = 100.15;
  const padX = 8, padY = 8;
  const chartW = W - padX * 2, chartH = H - padY * 2;

  const mapY = (v) => padY + chartH - ((v - yMin) / (yMax - yMin)) * chartH;
  const mapX = (i) => padX + (i / (monthlyData.length - 1)) * chartW;

  const linePts = monthlyData.map((v, i) => `${mapX(i)},${mapY(v)}`).join(' ');
  const areaD = `M ${mapX(0)},${H} L ${linePts.split(' ').join(' L ')} L ${mapX(monthlyData.length - 1)},${H} Z`;
  const lineD = `M ${linePts.split(' ').join(' L ')}`;

  useEffect(() => {
    const el = cardRef.current;
    const valueEl = valueRef.current;
    if (!el || !valueEl) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(el, { opacity: 0, y: 30 }, {
        opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
      });
      const obj = { v: 0 };
      gsap.to(obj, {
        v: stat.value, duration: 1.6, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
        onUpdate: () => { valueEl.textContent = `${obj.v.toFixed(1)}${stat.suffix ?? ''}`; },
      });
      if (clipRectRef.current) {
        gsap.fromTo(clipRectRef.current,
          { attr: { width: 0 } },
          {
            attr: { width: W },
            duration: 1.8, ease: 'power2.inOut', delay: 0.3,
            scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
          }
        );
      }
    }, el);
    return () => ctx.revert();
  }, [stat.value, stat.suffix]);

  return (
    <motion.div
      ref={cardRef}
      data-telemetry-card="true"
      className="group relative flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-800/60 bg-gray-950/20 backdrop-blur-md p-6"
      whileHover={{ y: -6 }}
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_20%_20%,rgba(244,125,17,0.18),transparent_45%),radial-gradient(circle_at_85%_65%,rgba(244,115,58,0.14),transparent_45%)]" />
      <div className="relative flex flex-shrink-0 items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-400">{stat.kicker}</div>
          <div ref={valueRef} className="mt-2 text-4xl font-black text-white">
            {stat.value.toFixed(1)}{stat.suffix ?? ''}
          </div>
          <div className="mt-1 text-sm text-gray-300">{stat.label}</div>
        </div>
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#F47D11]/15 to-[#F4733A]/15 border border-[#F47D11]/20 flex items-center justify-center text-xl">
          {stat.icon}
        </div>
      </div>

      <div className="mt-4 flex min-h-0 w-full flex-1 flex-col">
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="none" className="block w-full flex-1 basis-0 min-h-[5rem]" aria-hidden="true">
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={neon.a} stopOpacity="0.4" />
              <stop offset="100%" stopColor={neon.a} stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
              <stop stopColor={neon.a} />
              <stop offset="1" stopColor={neon.b} />
            </linearGradient>
            <filter id="lineGlow" x="-10%" y="-50%" width="120%" height="200%">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <clipPath id="chartClip">
              <rect ref={clipRectRef} x="0" y="0" width="0" height={H} />
            </clipPath>
          </defs>

          <line x1={padX} y1={padY} x2={W - padX} y2={padY} stroke="rgba(244,125,17,0.08)" strokeWidth="1" strokeDasharray="3 4" />
          <line x1={padX} y1={H / 2} x2={W - padX} y2={H / 2} stroke="rgba(244,125,17,0.06)" strokeWidth="1" />

          <g clipPath="url(#chartClip)">
            <path d={areaD} fill="url(#areaGrad)" />
            <path d={lineD} fill="none" stroke="url(#lineGrad)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" filter="url(#lineGlow)" />
            {monthlyData.map((v, i) => (
              <circle key={i} cx={mapX(i)} cy={mapY(v)} r="3" fill={neon.a} filter="url(#lineGlow)" />
            ))}
          </g>
        </svg>
      </div>

      <div className="mt-3 flex flex-shrink-0 justify-between items-center pt-1">
        <div className="flex flex-wrap gap-x-2 gap-y-1">
          {labels.filter((_, i) => i % 2 === 0).map((l) => (
            <span key={l} className="text-[10px] text-gray-600">{l}</span>
          ))}
        </div>
        <div className="inline-flex items-center gap-1.5 text-xs text-green-400">
          <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
          12-month trend
        </div>
      </div>
    </motion.div>
  );
};

// ─── 247Care weekly activity bar chart (wide strip under hero copy) ────────────
const CareActivityBarChart = () => {
  const containerRef = useRef(null);
  const barRefs = useRef([]);

  const series = useMemo(
    () => [
      { label: 'Mon', value: 68 },
      { label: 'Tue', value: 82 },
      { label: 'Wed', value: 74 },
      { label: 'Thu', value: 91 },
      { label: 'Fri', value: 86 },
      { label: 'Sat', value: 58 },
      { label: 'Sun', value: 52 },
    ],
    []
  );
  const maxVal = useMemo(() => Math.max(...series.map((s) => s.value), 1), [series]);

  useEffect(() => {
    const card = containerRef.current?.closest('[data-telemetry-card="true"]');
    if (!card) return undefined;

    const ctx = gsap.context(() => {
      const bars = barRefs.current.filter(Boolean);
      gsap.fromTo(
        bars,
        { scaleY: 0, transformOrigin: 'bottom center' },
        {
          scaleY: 1,
          duration: 0.85,
          stagger: 0.07,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, card);

    return () => ctx.revert();
  }, [series, maxVal]);

  return (
    <div
      ref={containerRef}
      className="flex h-full min-h-0 w-full flex-1 flex-col"
      role="img"
      aria-label="Weekly support load index from Monday through Sunday"
    >
      <div className="mb-2 flex flex-shrink-0 items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-widest text-gray-500">Support load index</span>
        <span className="text-[10px] text-gray-600">Last 7 days</span>
      </div>
      <div className="relative flex min-h-0 flex-1 flex-col rounded-xl border border-gray-800/50 bg-gray-900/25 px-3 pt-4 pb-2">
        <div
          className="pointer-events-none absolute inset-x-3 top-4 bottom-8 flex flex-col justify-between"
          aria-hidden
        >
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="border-t border-dashed border-[rgba(244,125,17,0.08)]" />
          ))}
        </div>
        <div className="relative flex min-h-[7rem] flex-1 items-end justify-between gap-1.5 sm:gap-2 sm:min-h-[8rem]">
          {series.map((row, i) => {
            const heightPct = (row.value / maxVal) * 100;
            return (
              <div key={row.label} className="flex-1 min-w-0 flex flex-col items-center justify-end h-full">
                <motion.div
                  ref={(el) => { barRefs.current[i] = el; }}
                  className="w-full max-w-[48px] mx-auto rounded-t-md"
                  style={{
                    height: `${heightPct}%`,
                    minHeight: 6,
                    background: `linear-gradient(180deg, ${neon.a} 0%, ${neon.b} 55%, #CC5C14 100%)`,
                    boxShadow: '0 0 16px rgba(244, 125, 17, 0.35), inset 0 1px 0 rgba(255,255,255,0.12)',
                    transform: 'scaleY(0)',
                    transformOrigin: 'bottom center',
                  }}
                />
              </div>
            );
          })}
        </div>
        <div className="relative mt-2 flex justify-between gap-1.5 sm:gap-2 px-0.5">
          {series.map((row) => (
            <div key={row.label} className="flex-1 min-w-0 text-center">
              <span className="text-[10px] font-semibold text-gray-500">{row.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Radial Gauge (center operational readiness) ───────────────────────────────
const RadialGauge = ({ value = 75, label = 'Health' }) => {
  const circleRef = useRef(null);
  const valueRef = useRef(null);

  useEffect(() => {
    const circle = circleRef.current;
    const valueEl = valueRef.current;
    if (!circle || !valueEl) return undefined;

    const r = 44;
    const c = 2 * Math.PI * r;
    circle.style.strokeDasharray = `${c}`;
    circle.style.strokeDashoffset = `${c}`;

    const ctx = gsap.context(() => {
      const obj = { v: 0 };
      gsap.to(obj, {
        v: value,
        duration: 1.4,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: circle.closest('[data-telemetry-card="true"]'),
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
        onUpdate: () => {
          const pct = Math.max(0, Math.min(100, obj.v));
          const offset = c * (1 - pct / 100);
          circle.style.strokeDashoffset = `${offset}`;
          valueEl.textContent = `${pct.toFixed(0)}%`;
        },
      });
    }, circleRef);

    return () => ctx.revert();
  }, [value]);

  return (
    <div className="flex items-center gap-4">
      <svg width="110" height="110" viewBox="0 0 110 110">
        <defs>
          <linearGradient id="ring" x1="0" y1="0" x2="110" y2="110" gradientUnits="userSpaceOnUse">
            <stop stopColor={neon.a} stopOpacity="0.95" />
            <stop offset="1" stopColor={neon.b} stopOpacity="0.9" />
          </linearGradient>
          <filter id="gaugeGlow">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle cx="55" cy="55" r="44" stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
        <circle
          cx="55" cy="55" r="44"
          stroke="url(#ring)"
          strokeWidth="10"
          strokeLinecap="round"
          fill="none"
          transform="rotate(-90 55 55)"
          filter="url(#gaugeGlow)"
          ref={circleRef}
        />
        <circle cx="55" cy="55" r="34" fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.06)" />
        <text x="55" y="60" textAnchor="middle" fill="white" fontSize="18" fontWeight="700" ref={valueRef}>
          0%
        </text>
      </svg>
      <div>
        <div className="text-xs uppercase tracking-widest text-gray-400">{label}</div>
        <div className="mt-1 text-sm text-gray-300">How your critical services are performing right now</div>
        <div className="mt-2 inline-flex items-center gap-2 text-xs text-gray-400">
          <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
          signal stable
        </div>
      </div>
    </div>
  );
};

// ─── Platform signals (fills space under Operational readiness) ─────────────
const PlatformSignalsCard = () => {
  const cardRef = useRef(null);

  const signals = useMemo(
    () => [
      { name: 'Internet & office links', hint: 'The connections that keep your teams and sites online', status: 'Stable' },
      { name: 'Text messaging (SMS)', hint: 'Bulk texts and alerts reaching mobile phones', status: 'Delivering' },
      { name: 'Phone calls', hint: 'Voice quality for customers and staff', status: 'Clear' },
      { name: 'Websites & sign-in', hint: 'Customer portals, apps, and secure logins', status: 'Online' },
    ],
    []
  );

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(el, { opacity: 0, y: 22 }, {
        opacity: 1, y: 0, duration: 0.75, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none reverse' },
      });
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <motion.div
      ref={cardRef}
      data-telemetry-card="true"
      className="group relative flex min-h-[220px] flex-1 flex-col overflow-hidden rounded-3xl border border-gray-800/60 bg-gray-950/20 backdrop-blur-md p-6"
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 bg-[radial-gradient(circle_at_70%_0%,rgba(244,125,17,0.14),transparent_45%),radial-gradient(circle_at_10%_100%,rgba(244,115,58,0.1),transparent_50%)]" />
      <div className="relative flex h-full min-h-0 flex-col">
        <div className="flex flex-shrink-0 items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-gray-400">What we watch</div>
            <div className="mt-1 text-lg font-bold text-white">Your services, in plain view</div>
            <p className="mt-1 max-w-md text-xs leading-relaxed text-gray-500">
              A quick snapshot of the main things customers rely on—so you can see everything is working as expected.
            </p>
          </div>
          <div className="flex-shrink-0 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
            All good
          </div>
        </div>

        <ul className="mt-4 flex min-h-0 flex-1 flex-col justify-between gap-2 sm:gap-2.5">
          {signals.map((s) => (
            <li
              key={s.name}
              className="flex items-center gap-3 rounded-2xl border border-gray-800/55 bg-gray-900/25 px-3 py-2.5 sm:px-3.5 sm:py-3"
            >
              <span
                className="h-2 w-2 flex-shrink-0 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.45)]"
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white">{s.name}</div>
                <div className="mt-0.5 text-[11px] text-gray-500">{s.hint}</div>
              </div>
              <div className="flex-shrink-0 text-right text-[11px] font-semibold text-[#F4A35C]">{s.status}</div>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

// ─── Parallax floating tech elements ──────────────────────────────────────────
const ParallaxTechElements = () => {
  const circuit = useParallax({ rotate: [0, 360], opacity: [0.3, 0.8, 0.3], scale: [0.9, 1.1, 0.9] });
  const server = useParallax({ translateY: [-30, 30], rotate: [-15, 15], opacity: [0.4, 0.9, 0.4] });
  const network = useParallax({ rotate: [360, 0], translateX: [-20, 20], opacity: [0.35, 0.85, 0.35] });
  const dataStream = useParallax({ translateY: [40, -40], scale: [0.95, 1.05, 0.95], opacity: [0.3, 0.7, 0.3] });
  const shield = useParallax({ translateY: [-20, 20], rotate: [-8, 8], opacity: [0.4, 0.85, 0.4] });
  const cloud = useParallax({ translateX: [-30, 30], translateY: [-15, 15], opacity: [0.35, 0.75, 0.35] });

  return (
    <>
      <div ref={circuit.ref} className="absolute top-4 left-0 -translate-x-1/2 pointer-events-none hidden lg:block" style={{ zIndex: 0 }}>
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none" className="drop-shadow-[0_0_12px_rgba(244,125,17,0.4)]">
          <rect x="10" y="10" width="60" height="60" rx="4" stroke={neon.a} strokeWidth="2" fill="none" />
          <circle cx="20" cy="20" r="4" fill={neon.a} />
          <circle cx="60" cy="20" r="4" fill={neon.b} />
          <circle cx="20" cy="60" r="4" fill={neon.b} />
          <circle cx="60" cy="60" r="4" fill={neon.a} />
          <line x1="20" y1="20" x2="40" y2="40" stroke={neon.a} strokeWidth="2" />
          <line x1="60" y1="20" x2="40" y2="40" stroke={neon.b} strokeWidth="2" />
          <line x1="20" y1="60" x2="40" y2="40" stroke={neon.b} strokeWidth="2" />
          <line x1="60" y1="60" x2="40" y2="40" stroke={neon.a} strokeWidth="2" />
          <circle cx="40" cy="40" r="6" fill={neon.a} opacity="0.8" />
        </svg>
      </div>

      <div ref={server.ref} className="absolute top-4 right-0 translate-x-1/2 pointer-events-none hidden lg:block" style={{ zIndex: 0 }}>
        <svg width="70" height="90" viewBox="0 0 70 90" fill="none" className="drop-shadow-[0_0_12px_rgba(244,115,58,0.4)]">
          <rect x="10" y="5" width="50" height="20" rx="2" stroke={neon.b} strokeWidth="2" fill="rgba(244,125,17,0.1)" />
          <rect x="10" y="30" width="50" height="20" rx="2" stroke={neon.a} strokeWidth="2" fill="rgba(244,115,58,0.1)" />
          <rect x="10" y="55" width="50" height="20" rx="2" stroke={neon.b} strokeWidth="2" fill="rgba(244,125,17,0.1)" />
          <circle cx="18" cy="15" r="2" fill={neon.a} /><circle cx="18" cy="40" r="2" fill={neon.b} /><circle cx="18" cy="65" r="2" fill={neon.a} />
          <rect x="25" y="12" width="25" height="6" rx="1" fill={neon.a} opacity="0.3" />
          <rect x="25" y="37" width="25" height="6" rx="1" fill={neon.b} opacity="0.3" />
          <rect x="25" y="62" width="25" height="6" rx="1" fill={neon.a} opacity="0.3" />
        </svg>
      </div>

      <div ref={network.ref} className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 pointer-events-none hidden lg:block" style={{ zIndex: 0 }}>
        <svg width="100" height="100" viewBox="0 0 100 100" fill="none" className="drop-shadow-[0_0_12px_rgba(244,125,17,0.4)]">
          <circle cx="50" cy="50" r="8" fill={neon.a} />
          <circle cx="50" cy="50" r="15" stroke={neon.a} strokeWidth="1.5" fill="none" opacity="0.6" />
          <circle cx="50" cy="50" r="25" stroke={neon.b} strokeWidth="1" fill="none" opacity="0.4" />
          <circle cx="20" cy="30" r="5" fill={neon.b} /><circle cx="80" cy="30" r="5" fill={neon.a} />
          <circle cx="20" cy="70" r="5" fill={neon.a} /><circle cx="80" cy="70" r="5" fill={neon.b} />
          <line x1="25" y1="32" x2="42" y2="45" stroke={neon.b} strokeWidth="1.5" opacity="0.6" />
          <line x1="75" y1="32" x2="58" y2="45" stroke={neon.a} strokeWidth="1.5" opacity="0.6" />
          <line x1="25" y1="68" x2="42" y2="55" stroke={neon.a} strokeWidth="1.5" opacity="0.6" />
          <line x1="75" y1="68" x2="58" y2="55" stroke={neon.b} strokeWidth="1.5" opacity="0.6" />
        </svg>
      </div>

      <div ref={dataStream.ref} className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 pointer-events-none hidden lg:block" style={{ zIndex: 0 }}>
        <svg width="60" height="100" viewBox="0 0 60 100" fill="none" className="drop-shadow-[0_0_12px_rgba(244,115,58,0.4)]">
          <path d="M30 10 Q40 30 30 50 Q20 70 30 90" stroke={neon.b} strokeWidth="3" fill="none" opacity="0.7" />
          <circle cx="30" cy="10" r="4" fill={neon.a} /><circle cx="30" cy="30" r="3" fill={neon.b} opacity="0.8" />
          <circle cx="30" cy="50" r="5" fill={neon.a} opacity="0.9" /><circle cx="30" cy="70" r="3" fill={neon.b} opacity="0.8" />
          <circle cx="30" cy="90" r="4" fill={neon.a} />
          <rect x="10" y="48" width="8" height="4" rx="1" fill={neon.a} opacity="0.6" />
          <rect x="42" y="48" width="8" height="4" rx="1" fill={neon.b} opacity="0.6" />
        </svg>
      </div>

      <div ref={shield.ref} className="absolute bottom-4 left-0 -translate-x-1/2 pointer-events-none hidden lg:block" style={{ zIndex: 0 }}>
        <svg width="75" height="85" viewBox="0 0 75 85" fill="none" className="drop-shadow-[0_0_12px_rgba(244,125,17,0.4)]">
          <path d="M37.5 5 L60 15 L60 40 Q60 65 37.5 80 Q15 65 15 40 L15 15 Z" stroke={neon.a} strokeWidth="2.5" fill="rgba(244,125,17,0.08)" />
          <path d="M30 40 L35 45 L45 32" stroke={neon.a} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="37.5" cy="25" r="3" fill={neon.b} />
        </svg>
      </div>

      <div ref={cloud.ref} className="absolute bottom-4 right-0 translate-x-1/2 pointer-events-none hidden lg:block" style={{ zIndex: 0 }}>
        <svg width="90" height="60" viewBox="0 0 90 60" fill="none" className="drop-shadow-[0_0_12px_rgba(244,115,58,0.4)]">
          <path d="M25 40 Q25 30 35 30 Q35 22 45 22 Q55 22 55 30 Q65 30 65 40 Q65 50 55 50 L35 50 Q25 50 25 40 Z" stroke={neon.b} strokeWidth="2" fill="rgba(244,115,58,0.1)" />
          <circle cx="35" cy="38" r="2" fill={neon.a} opacity="0.7" />
          <circle cx="45" cy="38" r="2" fill={neon.b} opacity="0.7" />
          <circle cx="55" cy="38" r="2" fill={neon.a} opacity="0.7" />
          <line x1="40" y1="32" x2="40" y2="44" stroke={neon.a} strokeWidth="1.5" opacity="0.5" />
          <line x1="50" y1="32" x2="50" y2="44" stroke={neon.b} strokeWidth="1.5" opacity="0.5" />
        </svg>
      </div>
    </>
  );
};

// ─── Main Stats Section ────────────────────────────────────────────────────────
const Stats = () => {
  const sectionRef = useRef(null);
  const stats = useMemo(
    () => [
      {
        kicker: 'CLIENTS',
        label: 'Businesses supported across sectors',
        value: 20,
        suffix: '+',
        icon: '👥',
      },
      {
        kicker: 'DEPLOYMENTS',
        label: 'Projects shipped end‑to‑end',
        value: 60,
        suffix: '+',
        icon: '🚀',
      },
      {
        kicker: 'SMS DELIVERED',
        label: 'Bulk SMS messages delivered',
        value: 500,
        suffix: 'M+',
        icon: '💬',
      },
      {
        kicker: 'VOICE CALLS',
        label: 'Voice minutes and calls delivered',
        value: 2,
        suffix: 'M+',
        icon: '📞',
      },
      {
        kicker: 'Uptime',
        label: 'DR-ready connectivity & ops',
        value: 99.9,
        suffix: '%',
        isFloat: true,
        icon: '⚡',
      },
    ],
    []
  );

  return (
    <section
      ref={sectionRef}
      className="relative py-16 sm:py-20 lg:py-28 px-4 sm:px-6 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #070707 0%, #14142c 55%, #0b1324 100%)' }}
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(244,125,17,.03)_1px,transparent_1px),linear-gradient(90deg,rgba(244,125,17,.03)_1px,transparent_1px)] bg-[size:70px_70px]" />
        <div
          className="hidden sm:block absolute -top-24 -right-24 w-[560px] h-[560px] rounded-full blur-3xl opacity-40"
          style={{ background: 'radial-gradient(circle, rgba(244,125,17,0.18), transparent 60%)' }}
        />
        <div
          className="hidden sm:block absolute -bottom-28 -left-28 w-[620px] h-[620px] rounded-full blur-3xl opacity-35"
          style={{ background: 'radial-gradient(circle, rgba(244,115,58,0.16), transparent 60%)' }}
        />
      </div>

      <ParallaxTechElements />

      <div className="container mx-auto relative z-10">
        <div className="max-w-4xl mx-auto text-center" data-reveal="up">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-[#F47D11]/12 to-[#F4733A]/10 backdrop-blur-sm px-6 py-3 rounded-full border border-[#F47D11]/22">
            <div className="w-2 h-2 bg-[#F47D11] rounded-full animate-pulse" />
            <span className="text-[#F47D11] font-semibold text-sm tracking-wider">Telemetry</span>
            <span className="text-gray-500 text-xs">/</span>
            <span className="text-gray-300 text-xs uppercase tracking-widest">Mission Control</span>
          </div>

          <h3 className="mt-6 text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight">
            Proof, not promises —
            <span className="block bg-gradient-to-r from-[#F47D11] via-[#F4733A] to-[#F47D11] bg-clip-text text-transparent">
              live signals of delivery.
            </span>
          </h3>
          <p className="mt-4 text-gray-300">
            Security-first deployments, resilient networks, and always-on operations — visualized like a modern ops console.
          </p>
        </div>

        <div className="mt-10 sm:mt-12 space-y-5 sm:space-y-6" data-reveal="stagger">
          {/* Row 1 */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-6 items-stretch">

            {/* Left column: Radar + Donut — tall stack beside Readiness */}
            <div className="lg:col-span-3 flex flex-col gap-6 h-full min-h-0 lg:min-h-[540px]">
              <div data-reveal-child className="lg:flex-1 lg:min-h-0 flex flex-col">
                <RadarChartCard stat={stats[0]} />
              </div>
              <div data-reveal-child className="lg:flex-1 lg:min-h-0 flex flex-col">
                <DonutChartCard stat={stats[1]} />
              </div>
            </div>

            {/* Center: Operational readiness + platform signals (fills column vs side stacks) */}
            <div className="lg:col-span-6 flex h-full min-h-0 w-full flex-col lg:min-h-[540px]">
              <motion.div
                data-telemetry-card="true"
                className="relative flex-shrink-0 overflow-hidden rounded-3xl border border-gray-800/60 bg-gray-950/20 backdrop-blur-md p-7 min-h-[280px] flex flex-col"
                whileHover={{ y: -6 }}
                transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                data-reveal-child
              >
                <div className="absolute inset-0 opacity-60 bg-[radial-gradient(circle_at_20%_20%,rgba(244,125,17,0.16),transparent_45%),radial-gradient(circle_at_85%_65%,rgba(244,115,58,0.12),transparent_45%)]" />
                <div className="relative flex flex-col">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-gray-400">System status</div>
                      <div className="mt-2 text-2xl font-black text-white">Is everything running smoothly?</div>
                      <div className="mt-2 text-sm text-gray-300">
                        We track connectivity, cloud tools, your support channels, and backup plans—so problems surface before your customers feel them.
                      </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-xs text-gray-400">
                      <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                      no issues detected
                    </div>
                  </div>

                  <div className="mt-5 sm:mt-6">
                    <RadialGauge value={97} label="Overall health" />
                  </div>

                  <div className="mt-5 sm:mt-6 grid grid-cols-3 gap-3">
                    {[
                      { k: 'Security', v: 'Strong' },
                      { k: 'Response time', v: 'Fast' },
                      { k: 'Capacity', v: 'Scales up' },
                    ].map((t) => (
                      <div key={t.k} className="rounded-2xl border border-gray-800/60 bg-gray-900/20 p-4">
                        <div className="text-xs uppercase tracking-widest text-gray-400">{t.k}</div>
                        <div className="mt-2 text-sm font-semibold text-white">{t.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              <div className="mt-6 flex min-h-0 flex-1 flex-col" data-reveal-child>
                <PlatformSignalsCard />
              </div>
            </div>

            {/* Right column: match row height with left */}
            <div className="lg:col-span-3 flex flex-col gap-6 h-full min-h-0 lg:min-h-[540px]">
              <div data-reveal-child className="lg:flex-1 lg:min-h-0 flex flex-col">
                <HorizontalBarChartCard stat={stats[2]} />
              </div>
              <div data-reveal-child className="lg:flex-1 lg:min-h-0 flex flex-col">
                <SunburstChartCard stat={stats[3]} />
              </div>
            </div>
          </div>

          {/* Row 2: Uptime Area Chart + 247Care strip */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-6 lg:items-stretch">
            <div className="flex h-full min-h-0 flex-col lg:col-span-4" data-reveal-child>
              <UptimeAreaCard stat={stats[4]} />
            </div>
            <div className="flex h-full min-h-0 flex-col lg:col-span-8">
              <motion.div
                data-telemetry-card="true"
                className="relative flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-800/60 bg-gray-950/20 backdrop-blur-md p-6"
                whileHover={{ y: -6 }}
                transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                data-reveal-child
              >
                <div className="absolute inset-0 opacity-60 bg-[radial-gradient(circle_at_20%_20%,rgba(244,125,17,0.16),transparent_45%),radial-gradient(circle_at_85%_65%,rgba(244,115,58,0.12),transparent_45%)]" />
                <div className="relative flex flex-shrink-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-gray-400">247Care</div>
                    <div className="mt-2 text-xl font-black text-white">Always-on support</div>
                    <div className="mt-2 text-sm text-gray-300">
                      Ops monitoring, incident response, maintenance, and continuous improvement.
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="rounded-2xl border border-gray-800/60 bg-gray-900/20 px-5 py-4">
                      <div className="text-xs text-gray-400">Coverage</div>
                      <div className="text-white font-black text-2xl">24/7</div>
                    </div>
                    <div className="rounded-2xl border border-gray-800/60 bg-gray-900/20 px-5 py-4">
                      <div className="text-xs text-gray-400">Response</div>
                      <div className="text-white font-black text-2xl">&lt;1d</div>
                    </div>
                  </div>
                </div>
                <div className="relative mt-6 min-h-0 flex-1 flex flex-col">
                  <CareActivityBarChart />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Stats;
